import { useCallback, useRef, useState } from "react";
import type { AvatarHandle, SpeechResult } from "@/features/companion";
import { ONBOARDING_SPOKEN_INTRO } from "./marcusOnboardingIntroCopy";
import { VOICE_INTRO_START_FALLBACK_MS, VOICE_INTRO_TIMEOUT_MS } from "./marcusOnboardingFlow";

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, Math.max(0, ms)));
}

export function useMarcusOnboardingVoice() {
  const [voiceIntroActive, setVoiceIntroActive] = useState(false);
  const [liveAvatarConnected, setLiveAvatarConnected] = useState(false);
  const [liveAvatarSpeaking, setLiveAvatarSpeaking] = useState(false);
  const avatarRef = useRef<AvatarHandle | null>(null);
  const voiceIntroTimerRef = useRef<number | null>(null);
  const voiceIntroStartTimerRef = useRef<number | null>(null);
  const voiceIntroStartResolverRef = useRef<(() => void) | null>(null);

  const clearVoiceIntroTimer = useCallback(() => {
    if (voiceIntroTimerRef.current) {
      window.clearTimeout(voiceIntroTimerRef.current);
      voiceIntroTimerRef.current = null;
    }
  }, []);

  const clearVoiceIntroStartWait = useCallback(() => {
    if (voiceIntroStartTimerRef.current) {
      window.clearTimeout(voiceIntroStartTimerRef.current);
      voiceIntroStartTimerRef.current = null;
    }
    voiceIntroStartResolverRef.current = null;
  }, []);

  const resolveVoiceIntroStartWait = useCallback(() => {
    const resolve = voiceIntroStartResolverRef.current;
    if (!resolve) return;
    clearVoiceIntroStartWait();
    resolve();
  }, [clearVoiceIntroStartWait]);

  const waitForVoiceIntroStart = useCallback(
    (timeoutMs = VOICE_INTRO_START_FALLBACK_MS) =>
      new Promise<void>((resolve) => {
        clearVoiceIntroStartWait();
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          if (voiceIntroStartTimerRef.current) {
            window.clearTimeout(voiceIntroStartTimerRef.current);
            voiceIntroStartTimerRef.current = null;
          }
          voiceIntroStartResolverRef.current = null;
          resolve();
        };
        voiceIntroStartResolverRef.current = finish;
        voiceIntroStartTimerRef.current = window.setTimeout(finish, timeoutMs);
      }),
    [clearVoiceIntroStartWait],
  );

  const stopVoiceIntro = useCallback(() => {
    clearVoiceIntroTimer();
    resolveVoiceIntroStartWait();
    avatarRef.current?.clearQueue();
    setVoiceIntroActive(false);
    setLiveAvatarConnected(false);
    setLiveAvatarSpeaking(false);
  }, [clearVoiceIntroTimer, resolveVoiceIntroStartWait]);

  const scheduleVoiceIntroStop = useCallback(
    (delayMs = VOICE_INTRO_TIMEOUT_MS) => {
      clearVoiceIntroTimer();
      voiceIntroTimerRef.current = window.setTimeout(stopVoiceIntro, delayMs);
    },
    [clearVoiceIntroTimer, stopVoiceIntro],
  );

  const startVoiceIntro = useCallback((text = ONBOARDING_SPOKEN_INTRO) => {
    const avatar = avatarRef.current;
    if (!avatar?.start) return false;
    const trimmed = text.trim();
    if (!trimmed) return false;
    clearVoiceIntroStartWait();
    clearVoiceIntroTimer();
    setVoiceIntroActive(true);
    setLiveAvatarConnected(false);
    setLiveAvatarSpeaking(false);
    avatar.start();
    avatar.clearQueue();
    avatar.speak(trimmed);
    return true;
  }, [clearVoiceIntroStartWait, clearVoiceIntroTimer]);

  const speakVoiceIntro = useCallback((text = ONBOARDING_SPOKEN_INTRO) => {
    const avatar = avatarRef.current;
    const trimmed = text.trim();
    if (!avatar?.speak || !trimmed) return false;
    clearVoiceIntroStartWait();
    clearVoiceIntroTimer();
    setVoiceIntroActive(true);
    avatar.clearQueue();
    avatar.speak(trimmed);
    return true;
  }, [clearVoiceIntroStartWait, clearVoiceIntroTimer]);

  const prepareVoiceForOnboardingStep = useCallback(() => {
    clearVoiceIntroTimer();
    setVoiceIntroActive(true);
    const avatar = avatarRef.current;
    avatar?.start?.();
  }, [clearVoiceIntroTimer]);

  const speakOnboardingHighlightAndWait = useCallback(
    async (comment: string | undefined, fallbackMs: number): Promise<SpeechResult> => {
      if (!comment) {
        await waitMs(fallbackMs);
        return { status: "timeout", text: "", reason: "missing onboarding narration" };
      }
      clearVoiceIntroTimer();
      setVoiceIntroActive(true);
      const avatar = avatarRef.current;
      avatar?.start?.();
      if (avatar?.speakAndWait) {
        return avatar.speakAndWait(comment, { timeoutMs: fallbackMs });
      }
      avatar?.speak(comment);
      await waitMs(fallbackMs);
      return { status: "timeout", text: comment, reason: "avatar speech completion unavailable" };
    },
    [clearVoiceIntroTimer],
  );

  return {
    avatarRef,
    clearVoiceIntroStartWait,
    clearVoiceIntroTimer,
    liveAvatarConnected,
    liveAvatarSpeaking,
    prepareVoiceForOnboardingStep,
    resolveVoiceIntroStartWait,
    scheduleVoiceIntroStop,
    setLiveAvatarConnected,
    setLiveAvatarSpeaking,
    speakVoiceIntro,
    speakOnboardingHighlightAndWait,
    startVoiceIntro,
    stopVoiceIntro,
    voiceIntroActive,
    waitForVoiceIntroStart,
  };
}
